import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { transfersService } from '../services/transfersService';
import { providersService } from '../services/providersService';
import { driversService } from '../services/driversService';
import { customersService } from '../services/customersService';
import { pricingService } from '../services/pricingService';
import { providerGroupsService, ProviderGroup } from '../services/providerGroupsService';
import api from '../services/api';
import { Transfer, Guest, UserRole, TripType, Currency, Provider, Driver, VehicleType, ProviderType, Customer, PricingType } from '../types';
import { GuestModal } from '../components/GuestModal';
import { RootState } from '../store/store';
import { authService } from '../services/authService';
import { GooglePlacesAutocomplete } from '../components/GooglePlacesAutocomplete';

type TransferFormData = Omit<Transfer, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'netProfit'>;

export const NewTransferPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const repeatFrom: Transfer | undefined = (location.state as any)?.repeatFrom;
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.TRY);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [assignmentType, setAssignmentType] = useState<'driver' | 'provider' | 'none'>('none');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [enableProviderPrice, setEnableProviderPrice] = useState(false);
  const [autoProviderPrice] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [autoCustomerPrice, setAutoCustomerPrice] = useState<number | null>(null);
  const [providerGroups, setProviderGroups] = useState<ProviderGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');

  // Meet & Greet Fee states
  const [hasMeetGreet, setHasMeetGreet] = useState(false);
  const [orgMeetGreetFee, setOrgMeetGreetFee] = useState<number | null>(null);

  const canAddTransfer = user?.role === UserRole.MANAGER ||
                         user?.role === UserRole.ORG_ADMIN ||
                         user?.role === UserRole.PLATFORM_ADMIN ||
                         (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('add'));

  const canEditPricing = user?.role === UserRole.MANAGER ||
                         user?.role === UserRole.ORG_ADMIN ||
                         user?.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => {
    const loadData = async () => {
      try {
        const [providersData, driversData, vehicleTypesData, customersData, groupsData] = await Promise.all([
          providersService.getAll(true),
          driversService.getAll(true),
          api.get('/vehicle-types?activeOnly=true').then(res => res.data),
          customersService.getAll(true),
          providerGroupsService.getAll(),
        ]);
        setProviders(providersData);
        setDrivers(driversData);
        setVehicleTypes(vehicleTypesData);
        setCustomers(customersData);
        setProviderGroups(groupsData);

        try {
          const profile: any = await authService.getProfile();
          if (profile.organization?.meetGreetFee) {
            setOrgMeetGreetFee(Number(profile.organization.meetGreetFee));
          }
        } catch (err) {
          console.error('Failed to load org settings:', err);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  const currencySymbols: Record<Currency, string> = {
    [Currency.TRY]: '₺',
    [Currency.EUR]: '€',
    [Currency.USD]: '$',
    [Currency.GBP]: '£',
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TransferFormData>();

  const selectedTripType = watch('tripType');
  const watchPickupLocation = watch('pickupLocation');
  const watchDropoffLocation = watch('dropoffLocation');

  useEffect(() => {
    const lookup = async () => {
      if (!selectedCustomerId || !watchPickupLocation || !watchDropoffLocation) {
        setAutoCustomerPrice(null);
        return;
      }
      try {
        const result = await pricingService.lookupPrice(
          PricingType.CUSTOMER,
          selectedCustomerId,
          watchPickupLocation,
          watchDropoffLocation,
        );
        setAutoCustomerPrice(result ? Number(result.price) : null);
      } catch {
        setAutoCustomerPrice(null);
      }
    };
    lookup();
  }, [selectedCustomerId, watchPickupLocation, watchDropoffLocation]);

  // Pre-fill form when "Tekrarla" is used
  useEffect(() => {
    if (!repeatFrom) return;

    setValue('tripType', repeatFrom.tripType);
    setValue('transferTime', repeatFrom.transferTime);
    setValue('pickupLocation', repeatFrom.pickupLocation);
    setValue('dropoffLocation', repeatFrom.dropoffLocation);
    if (repeatFrom.flightCode) setValue('flightCode', repeatFrom.flightCode);
    if (repeatFrom.passengerCount) setValue('passengerCount', repeatFrom.passengerCount);
    if (repeatFrom.specialNotes) setValue('specialNotes', repeatFrom.specialNotes);
    if (repeatFrom.vehicleType) setValue('vehicleType', repeatFrom.vehicleType);
    if (repeatFrom.currency) setSelectedCurrency(repeatFrom.currency as any);

    if (repeatFrom.assignedProviderId) {
      setValue('assignedProviderId', repeatFrom.assignedProviderId);
      setAssignmentType('provider');
    } else if (repeatFrom.assignedDriverId) {
      setValue('assignedDriverId', repeatFrom.assignedDriverId);
      setAssignmentType('driver');
    }

    if (repeatFrom.providerPrice) {
      setValue('providerPrice', Number(repeatFrom.providerPrice));
      setEnableProviderPrice(true);
    }
    if (repeatFrom.meetGreetFee && Number(repeatFrom.meetGreetFee) > 0) {
      setValue('meetGreetFee', Number(repeatFrom.meetGreetFee));
      setHasMeetGreet(true);
    }

    if (repeatFrom.guests && repeatFrom.guests.length > 0) {
      setGuests(repeatFrom.guests.map(g => ({ name: g.name, phone: g.phone, email: g.email, tcPassport: g.tcPassport })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatFrom]);

  const handleGuestsAdded = (newGuests: Guest[]) => {
    setGuests(newGuests);
    toast.success(t('guest.guestsUpdated'));
  };

  const handleRemoveGuest = (index: number) => {
    const newGuests = guests.filter((_, i) => i !== index);
    setGuests(newGuests);
  };

  const onSubmit = async (data: TransferFormData) => {
    try {
      setLoading(true);

      const cleanedGuests = guests.length > 0
        ? guests.map(({ name, phone, email, tcPassport }) => ({
            name,
            phone: phone || undefined,
            email: email || undefined,
            tcPassport: tcPassport || undefined,
          }))
        : undefined;

      const transferData = {
        ...data,
        currency: selectedCurrency,
        providerPrice: isNaN(data.providerPrice as any) ? undefined : data.providerPrice,
        extraExpenses: isNaN(data.extraExpenses as any) ? undefined : data.extraExpenses,
        meetGreetFee: hasMeetGreet ? (isNaN(data.meetGreetFee as any) ? undefined : data.meetGreetFee) : undefined,
        tcPassport: data.tcPassport || undefined,
        assignedProviderId: data.assignedProviderId || undefined,
        assignedDriverId: data.assignedDriverId || undefined,
        whatsappDriverName: data.whatsappDriverName || undefined,
        whatsappDriverPhone: data.whatsappDriverPhone || undefined,
        whatsappDriverPlate: data.whatsappDriverPlate || undefined,
        providerDriverName: data.providerDriverName || undefined,
        providerDriverPhone: data.providerDriverPhone || undefined,
        providerPlate: data.providerPlate || undefined,
        flightCode: data.flightCode || undefined,
        country: data.country || undefined,
        specialNotes: data.specialNotes || undefined,
        guests: cleanedGuests,
        customerId: selectedCustomerId || undefined,
        customerPrice: isNaN(data.customerPrice as any) ? (autoCustomerPrice ?? undefined) : data.customerPrice,
        poolGroupId: assignmentType === 'none' ? (selectedGroupId || null) : null,
        customerName: undefined,
        customerPhone: undefined,
        customerEmail: undefined,
      };

      const result = await transfersService.create(transferData);
      toast.success(t('transfers.transferCreated'));
      navigate(`/app/transfers?view=${result.id}`);
    } catch (error: any) {
      let errorMessage = t('transfers.failedToCreate');
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(', ');
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!canAddTransfer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('transfers.accessDenied')}</h2>
          <p className="text-gray-600">{t('transfers.noPermissionToCreate')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('transfers.newTransfer')}</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">{t('transfers.createSubtitle')}</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">

          {/* 1. Transfer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.transferType')} *
            </label>
            <select
              {...register('tripType', { required: t('transfers.transferTypeRequired') })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('transfers.selectTransferType')}</option>
              <option value={TripType.ARRIVAL}>{t('transfers.arrival')}</option>
              <option value={TripType.DEPARTURE}>{t('transfers.departure')}</option>
            </select>
            {errors.tripType && (
              <p className="text-red-500 text-sm mt-1">{errors.tripType.message}</p>
            )}
          </div>

          {/* 1b. Kaynak Firma */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kaynak Firma
            </label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">Seçin (opsiyonel)</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* 2. Transfer Date & Time */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.transferDate')} *
              </label>
              <input
                type="date"
                {...register('transferDate', { required: t('transfers.dateRequired') })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              />
              {errors.transferDate && (
                <p className="text-red-500 text-xs mt-1">{errors.transferDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.transferTime')} *
              </label>
              <input
                type="time"
                {...register('transferTime', { required: t('transfers.timeRequired') })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
              />
              {errors.transferTime && (
                <p className="text-red-500 text-xs mt-1">{errors.transferTime.message}</p>
              )}
            </div>
          </div>

          {/* 3. Pickup Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.pickupLocation')} *
            </label>
            <GooglePlacesAutocomplete
              value={watchPickupLocation || ''}
              onChange={(value) => setValue('pickupLocation', value, { shouldValidate: true })}
              placeholder={t('transfers.placeholders.pickupExample')}
              error={!!errors.pickupLocation}
            />
            <input type="hidden" {...register('pickupLocation', { required: t('transfers.pickupRequired') })} />
            {errors.pickupLocation && (
              <p className="text-red-500 text-sm mt-1">{errors.pickupLocation.message}</p>
            )}
          </div>

          {/* 4. Dropoff Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.dropoffLocation')} *
            </label>
            <GooglePlacesAutocomplete
              value={watchDropoffLocation || ''}
              onChange={(value) => setValue('dropoffLocation', value, { shouldValidate: true })}
              placeholder={t('transfers.placeholders.dropoffExample')}
              error={!!errors.dropoffLocation}
            />
            <input type="hidden" {...register('dropoffLocation', { required: t('transfers.dropoffRequired') })} />
            {errors.dropoffLocation && (
              <p className="text-red-500 text-sm mt-1">{errors.dropoffLocation.message}</p>
            )}
          </div>

          {/* 5. Return Time (only for Departure) */}
          {selectedTripType === TripType.DEPARTURE && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.departureTime')}
              </label>
              <input
                type="time"
                {...register('returnTime')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* 6. Flight Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.flightCode')}
            </label>
            <input
              type="text"
              {...register('flightCode')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('transfers.placeholders.flightCodeExample')}
            />
          </div>

          {/* 7. Vehicle Type */}
          {canEditPricing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('transfers.vehicleType')}
              </label>
              <select
                {...register('vehicleType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('transfers.selectVehicleType')}</option>
                {vehicleTypes.map((vehicleType) => (
                  <option key={vehicleType.id} value={vehicleType.name}>
                    {vehicleType.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 8. Passenger Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.passengerCount')} *
            </label>
            <input
              type="number"
              min="1"
              {...register('passengerCount', {
                required: t('transfers.passengerCountRequired'),
                valueAsNumber: true,
                min: { value: 1, message: t('transfers.passengerCountMin') },
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="2"
            />
            {errors.passengerCount && (
              <p className="text-red-500 text-sm mt-1">{errors.passengerCount.message}</p>
            )}
          </div>

          {/* 9. Atama */}
          {user?.role !== UserRole.STAFF && (
            <div className="border-t pt-4 sm:pt-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">Kime Verilecek?</h3>

              <div className="flex gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => { setAssignmentType('none'); setSelectedProvider(null); }}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm transition-colors ${
                    assignmentType === 'none'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  🏊 Havuza At
                </button>
                <button
                  type="button"
                  onClick={() => setAssignmentType('provider')}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-medium text-sm transition-colors ${
                    assignmentType === 'provider'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  🎯 Direkt Ata
                </button>
              </div>

              {assignmentType === 'none' && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Havuz Grubu</label>
                    <select
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    >
                      <option value="">🌐 Genel Havuz (Herkes görür)</option>
                      {providerGroups.filter(g => g.isActive).map(g => (
                        <option key={g.id} value={g.id}>🏊 {g.name}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    {selectedGroupId
                      ? `Sadece "${providerGroups.find(g => g.id === selectedGroupId)?.name}" grubundaki tedarikçiler görür.`
                      : 'Tüm tedarikçiler görebilir, kim önce alırsa alır.'}
                  </p>
                </div>
              )}

              {assignmentType === 'provider' && (
                <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tedarikçi Seç
                    </label>
                    <select
                      {...register('assignedProviderId', {
                        onChange: (e) => {
                          const provider = providers.find(p => p.id === e.target.value);
                          setSelectedProvider(provider || null);
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Tedarikçi seçin</option>
                      {providers.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name} {provider.providerType === ProviderType.WHATSAPP ? '(WhatsApp)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedProvider?.providerType === ProviderType.WHATSAPP && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü Adı</label>
                        <input type="text" {...register('providerDriverName')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Sürücü adı soyadı" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sürücü Telefon</label>
                        <input type="tel" {...register('providerDriverPhone')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="+90 555 123 4567" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plaka</label>
                        <input type="text" {...register('providerPlate')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="34 ABC 123" />
                      </div>
                    </>
                  )}

                  {selectedProvider && selectedProvider.providerType !== ProviderType.WHATSAPP && (
                    <p className="text-sm text-purple-700">Tedarikçi kendi bilgilerini girecek.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 10. Pricing */}
          {canEditPricing && (
            <div className="border-t pt-4 sm:pt-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Fiyatlandırma</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Para Birimi
                  </label>
                  <select
                    {...register('currency')}
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value as Currency)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={Currency.TRY}>TRY (₺)</option>
                    <option value={Currency.EUR}>EUR (€)</option>
                    <option value={Currency.USD}>USD ($)</option>
                    <option value={Currency.GBP}>GBP (£)</option>
                  </select>
                </div>

                {/* Kaynak Firma Fiyatı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kaynak Firma Fiyatı
                    {autoCustomerPrice !== null && (
                      <span className="text-xs text-green-600 ml-2">(Otomatik: {autoCustomerPrice} TL)</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">{currencySymbols[selectedCurrency]}</span>
                    <input
                      type="number"
                      step="0.01"
                      {...register('customerPrice', { valueAsNumber: true })}
                      defaultValue={autoCustomerPrice ?? undefined}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Tedarikçi Fiyatı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tedarikçi Fiyatı
                    {autoProviderPrice !== null && !enableProviderPrice && (
                      <span className="text-xs text-green-600 ml-2">(Otomatik: {autoProviderPrice} TL)</span>
                    )}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">{currencySymbols[selectedCurrency]}</span>
                    <input
                      type="number"
                      step="0.01"
                      {...register('providerPrice', { valueAsNumber: true })}
                      disabled={autoProviderPrice !== null && !enableProviderPrice}
                      className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 ${
                        autoProviderPrice !== null && !enableProviderPrice
                          ? 'bg-green-50 border-green-300 text-gray-700'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('transfers.extraExpenses')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">
                      {currencySymbols[selectedCurrency]}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      {...register('extraExpenses', { valueAsNumber: true })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Meet & Greet Fee */}
                <div className="sm:col-span-2">
                  <div className="flex items-center space-x-3 mb-2">
                    <input
                      type="checkbox"
                      checked={hasMeetGreet}
                      onChange={(e) => {
                        setHasMeetGreet(e.target.checked);
                        if (e.target.checked && orgMeetGreetFee) {
                          setValue('meetGreetFee', orgMeetGreetFee);
                        } else if (!e.target.checked) {
                          setValue('meetGreetFee', undefined as any);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      {t('transfers.hasMeetGreet')}
                    </label>
                  </div>
                  {hasMeetGreet && (
                    <div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">&#8378;</span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('meetGreetFee', { valueAsNumber: true })}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      {orgMeetGreetFee && (
                        <p className="text-xs text-gray-500 mt-1">
                          {t('transfers.meetGreetFeeFromOrg', { fee: orgMeetGreetFee })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 11. Guests */}
          <div className="border-t pt-4 sm:pt-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">{t('transfers.guests')}</h3>
              <button
                type="button"
                onClick={() => setIsGuestModalOpen(true)}
                className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 font-medium text-sm sm:text-base min-h-[44px]"
              >
                {guests.length > 0 ? t('guest.editGuests') : `+ ${t('guest.addGuest')}`}
              </button>
            </div>

            {guests.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">{t('transfers.noGuestsYet')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {guests.map((guest, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{guest.name}</p>
                      <div className="text-sm text-gray-600 space-x-3">
                        {guest.phone && <span>📞 {guest.phone}</span>}
                        {guest.email && <span>✉️ {guest.email}</span>}
                        {guest.tcPassport && <span>🆔 {guest.tcPassport}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveGuest(index)}
                      className="ml-4 text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 12. Special Notes */}
          <div className="border-t pt-4 sm:pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('transfers.specialNotes')}
            </label>
            <textarea
              {...register('specialNotes')}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={t('transfers.placeholders.specialNotes')}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 border-t pt-4 sm:pt-6">
            <button
              type="button"
              onClick={() => navigate('/app/transfers')}
              className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium min-h-[44px]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 font-medium disabled:opacity-50 min-h-[44px]"
            >
              {loading ? t('transfers.creating') : t('transfers.createTransfer')}
            </button>
          </div>
        </form>
      </div>

      <GuestModal
        isOpen={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
        onGuestsAdded={handleGuestsAdded}
        existingGuests={guests}
      />
    </div>
  );
};
